/**
 * OrchestrationResult component for displaying orchestration outcomes
 * Shows success/error status with links to related resources
 */
import { cn } from '@/lib/utils'
import { 
  CheckCircle2, 
  XCircle, 
  ExternalLink,
  GitPullRequest,
  Play,
  Hash
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { MarkdownContent } from '@/components/common/MarkdownContent'

export interface OrchestrationResultProps {
  /** Whether the orchestration succeeded */
  success: boolean
  /** The result message (for success) */
  result?: string
  /** Error message (for failure) */
  error?: string
  /** ID of the created run, if any */
  runId?: string
  /** URL of the created PR, if any */
  prUrl?: string
  /** Session ID for reference */
  sessionId?: string
  /** Display variant */
  variant?: 'card' | 'inline'
  /** Additional CSS classes */
  className?: string
}

/**
 * Success content
 */
function SuccessContent({ 
  result, 
  runId, 
  prUrl, 
  sessionId,
  variant 
}: { 
  result?: string
  runId?: string
  prUrl?: string
  sessionId?: string
  variant: 'card' | 'inline'
}) {
  const isInline = variant === 'inline'

  return (
    <div className={cn('space-y-3', isInline && 'space-y-2')}>
      {/* Result message with markdown formatting */}
      {result && (
        <MarkdownContent 
          content={result}
          variant={isInline ? 'compact' : 'default'}
          className={isInline ? 'text-sm' : 'text-base'}
        />
      )}

      {/* Links */}
      <div className={cn(
        'flex flex-wrap gap-2',
        isInline && 'gap-3'
      )}>
        {runId && (
          <Button
            variant={isInline ? 'link' : 'outline'}
            size={isInline ? 'sm' : 'default'}
            asChild
          >
            <a href={`/runs/${runId}`}>
              <Play className="w-4 h-4 mr-2" />
              View Run Details
            </a>
          </Button>
        )}

        {prUrl && (
          <Button
            variant={isInline ? 'link' : 'outline'}
            size={isInline ? 'sm' : 'default'}
            asChild
          >
            <a href={prUrl} target="_blank" rel="noopener noreferrer">
              <GitPullRequest className="w-4 h-4 mr-2" />
              View Pull Request
              <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </Button>
        )}
      </div>

      {/* Session reference */}
      {sessionId && (
        <div className="flex items-center gap-2 text-muted-foreground text-xs mt-2">
          <Hash className="w-3 h-3" />
          <span className="font-mono">{sessionId}</span>
        </div>
      )}
    </div>
  )
}

/**
 * Error content
 */
function ErrorContent({ 
  error,
  sessionId,
  variant 
}: { 
  error?: string
  sessionId?: string
  variant: 'card' | 'inline'
}) {
  const isInline = variant === 'inline'

  return (
    <div className={cn('space-y-2', isInline && 'space-y-1')}>
      <p className={cn(
        'text-foreground',
        isInline ? 'text-sm' : 'text-base'
      )}>
        {error || 'An unexpected error occurred'}
      </p>

      {sessionId && (
        <div className="flex items-center gap-2 text-muted-foreground text-xs mt-2">
          <Hash className="w-3 h-3" />
          <span className="font-mono">{sessionId}</span>
        </div>
      )}
    </div>
  )
}

/**
 * Result component for orchestration outcomes.
 * 
 * Features:
 * - Success/error status display
 * - Links to run details
 * - Links to created PRs
 * - Session reference
 * 
 * @example
 * ```tsx
 * <OrchestrationResult
 *   success={true}
 *   result="Feature implemented"
 *   runId="run-123"
 *   prUrl="https://github.com/org/repo/pull/456"
 * />
 * ```
 */
export function OrchestrationResult({
  success,
  result,
  error,
  runId,
  prUrl,
  sessionId,
  variant = 'card',
  className
}: OrchestrationResultProps) {
  const isInline = variant === 'inline'
  const status = success ? 'success' : 'error'

  // Inline variant
  if (isInline) {
    return (
      <div
        role="alert"
        aria-label={`Orchestration ${status}`}
        data-status={status}
        data-variant="inline"
        className={cn(
          'flex items-start gap-3 p-3 rounded-lg border',
          success 
            ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
            : 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800',
          className
        )}
      >
        {success ? (
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
        ) : (
          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
        )}
        
        <div className="flex-1">
          {success ? (
            <SuccessContent 
              result={result} 
              runId={runId} 
              prUrl={prUrl} 
              sessionId={sessionId}
              variant="inline" 
            />
          ) : (
            <ErrorContent error={error} sessionId={sessionId} variant="inline" />
          )}
        </div>
      </div>
    )
  }

  // Card variant (default)
  return (
    <Card
      data-status={status}
      data-variant="card"
      className={cn(
        success 
          ? 'border-green-200 dark:border-green-800'
          : 'border-red-200 dark:border-red-800',
        className
      )}
    >
      <CardHeader className={cn(
        'pb-3',
        success 
          ? 'bg-green-50 dark:bg-green-950'
          : 'bg-red-50 dark:bg-red-950'
      )}>
        <div 
          role="alert"
          aria-label={`Orchestration ${status}`}
          className="flex items-center gap-2"
        >
          {success ? (
            <>
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="font-semibold text-green-800 dark:text-green-200">
                Success
              </span>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="font-semibold text-red-800 dark:text-red-200">
                Error
              </span>
            </>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-4">
        {success ? (
          <SuccessContent 
            result={result} 
            runId={runId} 
            prUrl={prUrl} 
            sessionId={sessionId}
            variant="card" 
          />
        ) : (
          <ErrorContent error={error} sessionId={sessionId} variant="card" />
        )}
      </CardContent>
    </Card>
  )
}
